import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  tablePlugin,
  toolbarPlugin,
  linkPlugin,
  imagePlugin,
  codeBlockPlugin,
  diffSourcePlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  InsertTable,
  InsertThematicBreak,
  InsertImage,
  InsertCodeBlock,
  CreateLink,
  ListsToggle,
  BlockTypeSelect,
  DiffSourceToggleWrapper,
  MDXEditorMethods,
  type RealmPlugin,
} from '@mdxeditor/editor';
import { Button, Card, theme as antdTheme } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useThemeContext } from "../contexts/ThemeContext";
import '@mdxeditor/editor/style.css';

export interface MarkdownEditorProps {
  /** Initial markdown content (for uncontrolled usage) */
  initialMarkdown?: string;
  /** Current markdown content (for controlled usage) */
  markdown?: string;
  /** Callback when content changes (for controlled usage) */
  onChange?: (markdown: string) => void;
  /** Callback when save button is clicked */
  onSave?: (markdown: string) => void;
  /** Whether the editor is disabled (read-only) */
  disabled?: boolean;
  /** Whether save button is loading */
  isSaving?: boolean;
  /** Title for the editor card */
  title?: string;
  /** Placeholder text when editor is empty */
  placeholder?: string;
  /** Additional className for styling */
  className?: string;
  /** Whether to show the save button */
  showSaveButton?: boolean;
  /** Save button text */
  saveButtonText?: string;
  /** Height of the editor */
  height?: string | number;
  /** Whether to enable diff source view (raw markdown toggle) */
  enableDiffSource?: boolean;
}

/**
 * A reusable markdown editor component based on MDXEditor.
 * Supports both controlled and uncontrolled usage patterns.
 * Includes a comprehensive toolbar with formatting options and table support.
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  initialMarkdown = "",
  markdown: controlledMarkdown,
  onChange,
  onSave,
  disabled = false,
  isSaving = false,
  title,
  placeholder = "Enter your markdown content here...",
  className = "",
  showSaveButton = true,
  saveButtonText = "Save Changes",
  height = 400,
  enableDiffSource = true,
}) => {
  const [internalMarkdown, setInternalMarkdown] = useState(initialMarkdown);
  const editorRef = useRef<MDXEditorMethods>(null);
  const { antdTheme: themeConfig } = useThemeContext();
  
  // Check if dark mode is enabled by looking at the antd theme algorithm
  const isDarkMode = themeConfig?.algorithm === antdTheme.darkAlgorithm;

  // Sync internal state when initialMarkdown prop changes
  useEffect(() => {
    setInternalMarkdown(initialMarkdown);
  }, [initialMarkdown]);

  // Determine if we're in controlled mode
  const isControlled = controlledMarkdown !== undefined;
  const currentMarkdown = isControlled ? controlledMarkdown : internalMarkdown;

  const handleChange = useCallback((newMarkdown: string) => {
    if (!isControlled) {
      setInternalMarkdown(newMarkdown);
    }
    onChange?.(newMarkdown);
  }, [isControlled, onChange]);

  const handleSave = useCallback(() => {
    onSave?.(currentMarkdown);
  }, [onSave, currentMarkdown]);

  // Build the toolbar contents based on options
  // Dynamic toolbar styling based on theme
  const toolbarDividerStyle = {
    width: '1px',
    height: '24px',
    backgroundColor: isDarkMode ? '#434343' : '#d9d9d9',
    margin: '0 8px'
  };

  const toolbarContents = (
    <>
      <UndoRedo />
      <div style={toolbarDividerStyle} />
      <BoldItalicUnderlineToggles />
      <div style={toolbarDividerStyle} />
      <ListsToggle />
      <CreateLink />
      <InsertTable />
      <InsertThematicBreak />
      <InsertImage />
      <InsertCodeBlock />
      <div style={toolbarDividerStyle} />
      <BlockTypeSelect />
      {enableDiffSource && (
        <>
          <div style={{ flexGrow: 1 }} />
          <DiffSourceToggleWrapper>
            <span style={{ fontSize: '12px', color: isDarkMode ? '#999' : '#666' }}>View Source</span>
          </DiffSourceToggleWrapper>
        </>
      )}
    </>
  );

  // Build the plugins array
  const plugins: RealmPlugin[] = [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    markdownShortcutPlugin(),
    tablePlugin(),
    linkPlugin(),
    imagePlugin(),
    codeBlockPlugin(),
    toolbarPlugin({ toolbarContents: () => toolbarContents }),
  ];

  // Add diff source plugin if enabled
  if (enableDiffSource) {
    plugins.push(diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: currentMarkdown }));
  }

  const editorContent = (
    <MDXEditor
      ref={editorRef}
      markdown={currentMarkdown}
      onChange={handleChange}
      readOnly={disabled}
      placeholder={placeholder}
      plugins={plugins}
      contentEditableClassName="mdx-editor-content"
      className={isDarkMode ? 'dark-theme dark-editor' : ''}
    />
  );

  const content = (
    <div className={`markdown-editor-wrapper ${className}`}>
      <div
        className={`editor-container ${disabled ? 'editor-disabled' : ''} ${isDarkMode ? 'dark-container' : ''}`}
        style={{
          height: typeof height === 'number' ? `${height}px` : height,
          border: `1px solid ${isDarkMode ? '#434343' : '#e5e7eb'}`,
          borderRadius: '8px',
          overflow: 'auto',
          backgroundColor: isDarkMode ? '#141414' : (disabled ? '#f5f5f5' : '#fff'),
        }}
      >
        <style>{`
          .mdx-editor-content {
            min-height: 100%;
          }
          .mdx-editor-content [contenteditable] {
            min-height: ${typeof height === 'number' ? height - 60 : `calc(${height} - 60px)`};
            max-height: ${typeof height === 'number' ? height - 60 : `calc(${height} - 60px)`};
            overflow-y: auto !important;
          }
          
          /* Dark theme for MDXEditor - Slate/Orange color scheme */
          .dark-editor {
            /* Orange accent colors */
            --accentBase: #1f1206;
            --accentBgSubtle: #2b1405;
            --accentBg: #391a03;
            --accentBgHover: #461f00;
            --accentBgActive: #532600;
            --accentLine: #6b3304;
            --accentBorder: #85420b;
            --accentBorderHover: #a5530e;
            --accentSolid: #f76808;
            --accentSolidHover: #ff802b;
            --accentText: #ff8f45;
            --accentTextContrast: #fff;

            /* Slate base colors */
            --baseBase: #101113;
            --baseBgSubtle: #18191b;
            --baseBg: #212225;
            --baseBgHover: #272a2d;
            --baseBgActive: #2e3135;
            --baseLine: #35373c;
            --baseBorder: #43484e;
            --baseBorderHover: #5a6169;
            --baseSolid: #696e77;
            --baseSolidHover: #7c818a;
            --baseText: #b0b4ba;
            --baseTextContrast: #edeef0;

            /* Admonition colors */
            --admonitionTipBg: #272a2d;
            --admonitionTipBorder: #5a6169;
            
            --admonitionInfoBg: #272a2d;
            --admonitionInfoBorder: #5a6169;

            --admonitionCautionBg: #272a2d;
            --admonitionCautionBorder: #5a6169;

            --admonitionDangerBg: #272a2d;
            --admonitionDangerBorder: #5a6169;

            --admonitionNoteBg: #272a2d;
            --admonitionNoteBorder: #5a6169;

            --basePageBg: #141414;
            background: var(--basePageBg);
          }
          
          /* Style toolbar for dark mode */
          .dark-editor .mdxeditor-toolbar {
            background-color: var(--slate-2);
            border-bottom: 1px solid var(--slate-6);
          }
          
          /* Style select dropdowns in dark mode */
          .dark-editor [role="combobox"],
          .dark-editor button[aria-haspopup="listbox"] {
            background-color: var(--slate-3);
            color: var(--slate-11);
            border-color: var(--slate-7);
          }
          
          /* Style toolbar buttons in dark mode */
          .dark-editor button[class*="toolbar"] {
            color: var(--slate-11);
          }
          
          .dark-editor button[class*="toolbar"]:hover {
            background-color: var(--slate-4);
          }
          
          /* Ensure content area has proper background in dark mode */
          .dark-editor .mdxeditor-root-contenteditable {
            background-color: var(--basePageBg);
          }
          
          .dark-editor .mdxeditor-root-contenteditable [contenteditable] {
            color: var(--slate-12);
          }
          
          /* Placeholder color in dark mode */
          .dark-editor .mdxeditor-root-contenteditable [contenteditable]:empty:before {
            color: var(--slate-9);
          }
          
          /* Popup/dropdown styling for dark mode */
          .mdxeditor-popup-container {
            z-index: 1100;
          }
        `}</style>
        {editorContent}
      </div>

      {showSaveButton && onSave && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isSaving}
            disabled={disabled}
          >
            {saveButtonText}
          </Button>
        </div>
      )}
    </div>
  );

  if (title) {
    return (
      <Card title={title} className="markdown-editor-card">
        {content}
      </Card>
    );
  }

  return content;
};

export default MarkdownEditor;
